import { apiClient } from "./api";

// Tipos para autenticación (V2 - PostgreSQL)
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

// Admin roles del backend
export type AdminRole = 'super_admin' | 'admin';
export type AdminStatus = 'active' | 'inactive' | 'suspended';

// Usuario autenticado (basado en AdminResponseDto del backend)
export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  fullName: string;
  role: AdminRole;
  status: AdminStatus;
  isActive: boolean;
  canManageUsers: boolean;
  canManageProducts: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
}

// Respuesta de login del backend
export interface AuthResponse {
  admin: User;          // El backend devuelve "admin" en lugar de "user"
  accessToken: string;  // "accessToken" en lugar de "token"
  refreshToken: string;
  expiresIn: number;    // Tiempo de expiración en segundos
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

class AuthService {
  private readonly TOKEN_KEY = "auth_token";
  private readonly REFRESH_TOKEN_KEY = "refresh_token";
  private readonly USER_KEY = "user_data";
  private authListeners = new Set<() => void>();

  // Suscripción a cambios de autenticación (login/logout/refresh)
  addAuthListener(listener: () => void): () => void {
    this.authListeners.add(listener);
    return () => {
      this.authListeners.delete(listener);
    };
  }

  private emitAuthChanged(): void {
    for (const listener of this.authListeners) {
      try {
        listener();
      } catch {
        // Ignorar errores de listeners para no romper el flujo de auth
      }
    }
  }

  // Login
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<
        {
          success: boolean;
          data: {
            admin: Partial<User> & {
              firstName?: string;
              lastName?: string;
              fullName?: string;
            };
            accessToken: string;
            refreshToken: string;
            expiresIn?: number;
          };
          message?: string;
        } | AuthResponse
      >("/admin/login", credentials);

      const payload =
        "data" in response && response?.data
          ? response.data
          : (response as AuthResponse);

      const rawAdmin = ("admin" in payload ? payload.admin : null) ?? null;
      const accessToken =
        "accessToken" in payload ? payload.accessToken : undefined;
      const refreshToken =
        "refreshToken" in payload ? payload.refreshToken : undefined;
      const expiresIn =
        "expiresIn" in payload && typeof payload.expiresIn === "number"
          ? payload.expiresIn
          : 0;

      if (!rawAdmin || !accessToken || !refreshToken) {
        throw new Error("Respuesta de autenticación inválida");
      }

      const firstName = rawAdmin.firstName ?? "";
      const lastName = rawAdmin.lastName ?? "";
      const derivedName = [firstName, lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
      const fallbackEmail = rawAdmin.email ?? "";
      const fullName =
        rawAdmin.fullName ?? (derivedName !== "" ? derivedName : fallbackEmail);

      const nowIso = new Date().toISOString();

      const admin: User = {
        id: rawAdmin.id ?? "",
        email: rawAdmin.email ?? "",
        firstName,
        lastName,
        fullName,
        role: (rawAdmin.role as AdminRole) ?? "admin",
        status: (rawAdmin.status as AdminStatus) ?? "active",
        isActive:
          typeof rawAdmin.isActive === "boolean"
            ? rawAdmin.isActive
            : ((rawAdmin.status as AdminStatus) ?? "active") === "active",
        canManageUsers:
          typeof rawAdmin.canManageUsers === "boolean"
            ? rawAdmin.canManageUsers
            : ((rawAdmin.role as AdminRole) ?? "admin") === "super_admin",
        canManageProducts:
          typeof rawAdmin.canManageProducts === "boolean"
            ? rawAdmin.canManageProducts
            : true,
        createdAt: rawAdmin.createdAt ?? nowIso,
        updatedAt: rawAdmin.updatedAt ?? nowIso,
        lastLoginAt: rawAdmin.lastLoginAt,
      };

      const authData: AuthResponse = {
        admin,
        accessToken,
        refreshToken,
        expiresIn,
      };

      this.setTokens(authData.accessToken, authData.refreshToken);
      this.setUser(authData.admin);
      this.emitAuthChanged();

      return authData;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  // Register (no disponible en el backend actual)
  async register(_data: RegisterData): Promise<AuthResponse> {
    throw new Error("Register no está disponible. Los administradores se crean desde el panel de admin.");
  }

  // Logout
  async logout(): Promise<void> {
    try {
      // Intentar hacer logout en el servidor
      await apiClient.post("/admin/logout");
    } catch {
      // Si falla, continuar con el logout local
    } finally {
      // Siempre limpiar datos locales
      this.clearAuth();
      this.emitAuthChanged();
    }
  }

  // Refresh token
  async refreshToken(): Promise<RefreshTokenResponse> {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        throw new Error("No hay refresh token disponible");
      }

      const response = await apiClient.post<RefreshTokenResponse>(
        "/admin/refresh",
        {
          refreshToken,
        }
      );

      const tokenData: RefreshTokenResponse = response;

      // Actualizar tokens
      this.setTokens(tokenData.accessToken, tokenData.refreshToken);
      this.emitAuthChanged();

      return tokenData;
    } catch (error) {
      // Si falla el refresh, hacer logout
      this.clearAuth();
      this.emitAuthChanged();
      throw this.handleAuthError(error);
    }
  }

  // Obtener perfil del usuario autenticado
  async getProfile(): Promise<User> {
    try {
      const response = await apiClient.get<User>("/admin/me");
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  // Verificar si está autenticado
  isAuthenticated(): boolean {
    const token = this.getToken();
    return !!token && !this.isTokenExpired(token);
  }

  // Verificar si es admin
  isAdmin(): boolean {
    if (!this.isAuthenticated()) return false;
    
    const user = this.getUser();
    return user !== null && user.isActive;
  }

  // Verificar si es super admin
  isSuperAdmin(): boolean {
    if (!this.isAuthenticated()) return false;
    
    const user = this.getUser();
    return user !== null && user.role === 'super_admin' && user.isActive;
  }

  // Validar token y obtener información
  validateToken(): { isValid: boolean; payload?: unknown; error?: string } {
    try {
      const token = this.getToken();

      if (!token) {
        return { isValid: false, error: "No hay token disponible" };
      }

      if (this.isTokenExpired(token)) {
        return { isValid: false, error: "Token expirado" };
      }

      // Decodificar payload del token
      const payload = this.decodeToken(token);
      return { isValid: true, payload };
    } catch (error) {
      return {
        isValid: false,
        error:
          error instanceof Error ? error.message : "Error al validar token",
      };
    }
  }

  // Decodificar token JWT
  decodeToken(token: string): unknown {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch {
      throw new Error("Token inválido");
    }
  }

  // Obtener tiempo de expiración del token
  getTokenExpiration(): Date | null {
    try {
      const token = this.getToken();
      if (!token) return null;

      const payload = this.decodeToken(token) as { exp: number };
      return new Date(payload.exp * 1000);
    } catch {
      return null;
    }
  }

  // Verificar si el token expira pronto (en los próximos 5 minutos)
  isTokenExpiringSoon(): boolean {
    try {
      const expiration = this.getTokenExpiration();
      if (!expiration) return false;

      const fiveMinutesFromNow = new Date(Date.now() + 5 * 60 * 1000);
      return expiration <= fiveMinutesFromNow;
    } catch {
      return false;
    }
  }

  // Guardar token manualmente (para casos especiales)
  setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  // Guardar refresh token manualmente
  setRefreshToken(refreshToken: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
  }

  // Obtener información del token sin validar
  getTokenInfo(): {
    hasToken: boolean;
    isExpired: boolean;
    expiration?: Date;
    payload?: unknown;
  } {
    const token = this.getToken();

    if (!token) {
      return { hasToken: false, isExpired: true };
    }

    try {
      const isExpired = this.isTokenExpired(token);
      const payload = this.decodeToken(token) as { exp: number };
      const expiration = new Date(payload.exp * 1000);

      return {
        hasToken: true,
        isExpired,
        expiration,
        payload,
      };
    } catch {
      return { hasToken: true, isExpired: true };
    }
  }

  // Obtener token actual
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  // Obtener refresh token
  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  // Obtener usuario actual
  getUser(): User | null {
    const userData = localStorage.getItem(this.USER_KEY);

    if (!userData) {
      return null;
    }

    try {
      const parsed = JSON.parse(userData) as User;

      if (parsed && typeof parsed === "object") {
        return parsed;
      }

      throw new Error("Invalid user data");
    } catch {
      localStorage.removeItem(this.USER_KEY);
      return null;
    }
  }

  // Verificar si el token está expirado
  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const expirationTime = payload.exp * 1000; // Convertir a milisegundos
      return Date.now() >= expirationTime;
    } catch {
      return true; // Si no se puede decodificar, considerar expirado
    }
  }

  // Establecer tokens
  private setTokens(token: string, refreshToken: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
  }

  // Establecer usuario
  private setUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  // Limpiar datos de autenticación
  private clearAuth(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
  }

  // Manejar errores de autenticación
  private handleAuthError(error: unknown): Error {
    if (typeof error === "object" && error !== null && "status" in error) {
      const errorObj = error as { status: number };
      if (errorObj.status === 401) {
        this.clearAuth();
        return new Error(
          "Sesión expirada. Por favor, inicia sesión nuevamente."
        );
      }
    }

    if (error instanceof Error) {
      return new Error(error.message || "Error de autenticación");
    }

    return new Error("Error de autenticación desconocido");
  }
}

// Instancia singleton
export const authService = new AuthService();
